import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { 
  Landmark, Calculator, CheckCircle, ArrowRight, Phone,
  FileText, Users, Clock, Shield, IndianRupee, Percent,
  Building2, Home, Briefcase
} from 'lucide-react';
import LocationSearch from '@/components/LocationSearch';

const processSteps = [
  { step: 1, title: 'Check Eligibility', description: 'Use our calculator to check your eligibility', icon: Calculator },
  { step: 2, title: 'Submit Documents', description: 'Upload required documents online', icon: FileText },
  { step: 3, title: 'Verification', description: 'Our team verifies your application', icon: Shield },
  { step: 4, title: 'Disbursement', description: 'Loan amount credited to your account', icon: Landmark },
];

const loanFeatures = [
  { icon: Percent, title: 'Competitive Rates', description: 'Starting from 8.5% p.a.' },
  { icon: Clock, title: 'Quick Approval', description: 'Approval within 48-72 hours' },
  { icon: Shield, title: 'Minimal Documentation', description: 'Simple and hassle-free process' },
  { icon: Users, title: 'Dedicated Support', description: 'Personal relationship manager' },
];

const loanTypes = [
  { value: 'home_purchase', label: 'Home Purchase Loan', icon: Home, description: 'For buying new or resale property' },
  { value: 'home_construction', label: 'Home Construction Loan', icon: Building2, description: 'For constructing your dream home' },
  { value: 'home_improvement', label: 'Home Improvement Loan', icon: Home, description: 'For renovation and repairs' },
  { value: 'balance_transfer', label: 'Balance Transfer', icon: Landmark, description: 'Transfer existing loan at better rates' },
  { value: 'top_up', label: 'Top-up Loan', icon: IndianRupee, description: 'Additional loan on existing home loan' },
];

const HomeLoans = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // EMI Calculator state
  const [loanAmount, setLoanAmount] = useState(5000000); // 50 Lakhs default
  const [interestRate, setInterestRate] = useState(8.5);
  const [tenure, setTenure] = useState(20); // 20 years default
  
  // Inquiry form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    loanType: 'home_purchase',
    employmentType: 'salaried',
    monthlyIncome: '',
    message: ''
  });

  // EMI Calculation
  const emiDetails = useMemo(() => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 12 / 100;
    const months = tenure * 12;
    
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    const totalPayment = emi * months;
    const totalInterest = totalPayment - principal;
    
    return {
      emi: Math.round(emi),
      totalPayment: Math.round(totalPayment),
      totalInterest: Math.round(totalInterest),
      principal
    };
  }, [loanAmount, interestRate, tenure]);

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/leads/home-loan', {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        city: formData.city || null,
        loan_amount: loanAmount,
        employment_type: formData.employmentType,
        message: formData.message || null
      });
      
      toast.success(`Application submitted! Your Request ID: ${response.data.request_id}`);
      
      // Open WhatsApp with prefilled message
      const whatsappMsg = `Hi ZenStudios! I'm interested in a home loan. My Request ID: ${response.data.request_id}. Loan Amount: ${formatCurrency(loanAmount)}`;
      window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
      
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="home-loans-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary/10 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Landmark className="w-4 h-4" /> Home Loans
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Turn Your Dream Home Into Reality
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Get the best home loan rates with minimal documentation and quick approvals. 
                ZenStudios partners with leading banks to help you secure your dream home.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary px-8"
                  onClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Calculate EMI <Calculator className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Apply Now <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              {loanFeatures.map((feature, idx) => (
                <div key={idx} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-600">Simple 4-step process to get your home loan</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {step.step}
                </div>
                <h3 className="font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMI Calculator */}
      <section className="py-20" id="calculator">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">EMI Calculator</h2>
            <p className="text-gray-600">Calculate your monthly EMI and plan your finances</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Calculator Inputs */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border">
              <div className="space-y-8">
                {/* Loan Amount */}
                <div>
                  <div className="flex justify-between mb-2">
                    <Label>Loan Amount</Label>
                    <span className="font-semibold text-primary">{formatCurrency(loanAmount)}</span>
                  </div>
                  <Slider
                    value={[loanAmount]}
                    onValueChange={(value) => setLoanAmount(value[0])}
                    min={500000}
                    max={50000000}
                    step={100000}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>₹5 L</span>
                    <span>₹5 Cr</span>
                  </div>
                </div>

                {/* Interest Rate */}
                <div>
                  <div className="flex justify-between mb-2">
                    <Label>Interest Rate (% p.a.)</Label>
                    <span className="font-semibold text-primary">{interestRate}%</span>
                  </div>
                  <Slider
                    value={[interestRate]}
                    onValueChange={(value) => setInterestRate(value[0])}
                    min={6}
                    max={15}
                    step={0.1}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>6%</span>
                    <span>15%</span>
                  </div>
                </div>

                {/* Tenure */}
                <div>
                  <div className="flex justify-between mb-2">
                    <Label>Loan Tenure</Label>
                    <span className="font-semibold text-primary">{tenure} Years</span>
                  </div>
                  <Slider
                    value={[tenure]}
                    onValueChange={(value) => setTenure(value[0])}
                    min={1}
                    max={30}
                    step={1}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>1 Year</span>
                    <span>30 Years</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-gradient-to-br from-primary to-primary/80 p-8 rounded-2xl text-white">
              <h3 className="text-xl font-semibold mb-6">Your EMI Details</h3>
              
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 mb-6">
                <p className="text-white/80 text-sm mb-2">Monthly EMI</p>
                <p className="text-4xl font-bold">{formatCurrency(emiDetails.emi)}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                  <p className="text-white/80 text-sm mb-1">Principal Amount</p>
                  <p className="text-xl font-semibold">{formatCurrency(emiDetails.principal)}</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                  <p className="text-white/80 text-sm mb-1">Total Interest</p>
                  <p className="text-xl font-semibold">{formatCurrency(emiDetails.totalInterest)}</p>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/20">
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Total Payment</span>
                  <span className="text-2xl font-bold">{formatCurrency(emiDetails.totalPayment)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Loan Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Types of Home Loans</h2>
            <p className="text-gray-600">Choose the right loan for your needs</p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {loanTypes.map((type, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-shadow text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <type.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{type.label}</h3>
                <p className="text-sm text-gray-600">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20" id="apply-form">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Apply for Home Loan</h2>
            <p className="text-gray-600">Fill in your details and our team will contact you within 24 hours</p>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg border">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter your name"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>City *</Label>
                  <LocationSearch
                    value={formData.city}
                    onChange={(val) => setFormData({...formData, city: val})}
                    placeholder="Search city..."
                    types="city,town"
                    required
                    className="rounded-xl mt-1"
                    data-testid="home-loans-city"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Loan Type</Label>
                  <Select value={formData.loanType} onValueChange={(v) => setFormData({...formData, loanType: v})}>
                    <SelectTrigger className="rounded-xl mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {loanTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Employment Type</Label>
                  <Select value={formData.employmentType} onValueChange={(v) => setFormData({...formData, employmentType: v})}>
                    <SelectTrigger className="rounded-xl mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salaried">Salaried</SelectItem>
                      <SelectItem value="self_employed">Self Employed</SelectItem>
                      <SelectItem value="business">Business Owner</SelectItem>
                      <SelectItem value="professional">Professional (Doctor/CA/Lawyer)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button type="submit" className="w-full rounded-full py-6 text-lg" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
              
              <p className="text-center text-sm text-gray-500">
                By submitting, you agree to our Terms &amp; Conditions. We&apos;ll contact you within 24 hours.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Help Choosing the Right Loan?</h2>
          <p className="text-white/80 mb-8">Our financial advisors are here to help you make the right decision</p>
          <Button 
            size="lg"
            className="rounded-full bg-white text-primary hover:bg-gray-100 px-8"
            onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20need%20help%20with%20home%20loan%20options.', '_blank')}
          >
            <Phone className="w-5 h-5 mr-2" />
            Talk to an Expert
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomeLoans;
